// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { personSchema } from '@/account/api/types';
import { fulfillmentOrderLineItemStatusSchema } from '@/constants/fulfillment-order-line-item-status-enum';
import { LocationCompanyType } from '@/constants/location-company-type';
import type { Sort } from '@/constants/sort';
import { contactSchema } from '@/contact/api/types';
import {
  type FulfillmentOrderPhase,
  type FulfillmentOrderStatus,
  type FulfillmentOrderSubstatus,
  fulfillmentOrderClosedAtSourceSchema,
  fulfillmentOrderStatusSchema,
} from '@/fulfillment/constants/fulfillment-order-status';
import type { FulfillmentSortProperty } from '@/fulfillment/hooks/use-fulfillments';
import { cycleTimeMetricSchema } from '@/report/api/types';
import {
  sparseCustomerSchema,
  sparseExchangeOrderSchema,
  sparseItemSchema,
  sparseLocationWithIntegrationSchema,
  sparseProjectSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { decimalDaysSchema } from '@/utilities/decimal-days-schema';
import { zodCatch } from '@/utilities/zod-catch';
import { z } from 'zod';

/**
 * Schema for a fulfillment order item.
 * Uses nested item structure where line item binding identifiers (id, key) are at the
 * top level, and catalog item properties are nested under `item`.
 */
export const fulfillmentOrderItemSchema = z.object({
  // Order line item identifiers (binding id/key)
  id: z.string(),
  key: z.string(),
  // Nested catalog item properties
  item: sparseItemSchema,
  // Quantity fields
  requestedQuantity: z.number(),
  pendingQuantity: z.number(),
  shippedQuantity: z.number(),
  // Units held in quarantine: reported by the third party but conflicting with
  // the asset record, so not shipped.
  quarantinedQuantity: z.number(),
  deliveredQuantity: z.number(),
  status: fulfillmentOrderLineItemStatusSchema,
});

export type FulfillmentOrderItem = z.infer<typeof fulfillmentOrderItemSchema>;

const contactSnapshotDataSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  company: sparseCustomerSchema.nullable().optional().default(null),
});

const contactSnapshotSchema = z.object({
  data: contactSnapshotDataSchema,
  savedAt: dateTimeSchema,
});

const orderDataSnapshotSchema = z.object({
  contact: contactSnapshotSchema.nullable(),
});

const fulfillmentOrderCycleTimeSchema = z.object({
  createdToOpenedDays: decimalDaysSchema,
  openToFirstShipmentShippedDays: decimalDaysSchema,
  firstShipmentShippedToCloseDays: decimalDaysSchema,
});

export type FulfillmentOrderCycleTime = z.infer<
  typeof fulfillmentOrderCycleTimeSchema
>;

/**
 * Schema for a fulfillment order's 3PL fulfillment and sync state.
 */
const fulfillmentOrderThirdPartySchema = z.object({
  isEnabled: z.boolean(),
  // Hardfin's last poll of the 3PL for this fulfillment order
  lastSyncedAt: dateTimeSchema.nullable(),
  // The 3PL's last reported data update for this fulfillment order
  lastUpdateAt: dateTimeSchema.nullable(),
  // Whether the 3PL binding is currently in an error state
  hasSyncError: z.boolean(),
  syncErrorMessage: z.string().nullable(),
});

/**
 * Schema for a fulfillment order.
 */
export const fulfillmentOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  organizationId: z.string(),
  fulfillmentOrderNumber: z.string(),
  project: sparseProjectSchema.nullable(),
  exchangeGroup: sparseExchangeOrderSchema.nullable(),
  fromLocation: sparseLocationWithIntegrationSchema.nullable(),
  toLocation: sparseLocationWithIntegrationSchema.nullable(),
  thirdParty: fulfillmentOrderThirdPartySchema,
  promiseShipDate: dateSchema.nullable(),
  promiseDeliveryDate: dateSchema.nullable(),
  status: fulfillmentOrderStatusSchema.catch(
    zodCatch<FulfillmentOrderStatus>('DRAFT')
  ),
  orderNotes: z.string().nullable(),
  contact: contactSchema.nullable(),
  orderDataSnapshot: orderDataSnapshotSchema.nullable(),
  items: z.array(fulfillmentOrderItemSchema),
  cancelledAt: dateTimeSchema.nullable(),
  cancelledBy: personSchema.nullable(),
  cancellationReason: z.string().nullable(),
  closedAt: dateTimeSchema.nullable(),
  closedAtSource: fulfillmentOrderClosedAtSourceSchema.nullable(),
  closedBy: personSchema.nullable(),
  openedAt: dateTimeSchema.nullable(),
  firstShipmentOpenedAt: dateTimeSchema.nullable(),
  lastShipmentOpenedAt: dateTimeSchema.nullable(),
  firstShipmentShippedAt: dateTimeSchema.nullable(),
  lastShipmentShippedAt: dateTimeSchema.nullable(),
  cycleTime: fulfillmentOrderCycleTimeSchema,
  mostRecentShippedAt: dateTimeSchema.nullable(),
  mostRecentDeliveredAt: dateTimeSchema.nullable(),
  createdAt: dateTimeSchema,
  createdBy: personSchema,
  lastUpdatedAt: dateTimeSchema,
  lastUpdatedBy: personSchema,
});

export type FulfillmentOrder = z.infer<typeof fulfillmentOrderSchema>;

/**
 * Filter parameters for listing fulfillment orders.
 *
 * The from/to location filters mirror the asset list: each side has three
 * independent dimensions (explicit location IDs, customer IDs, and location
 * company types), all of which are sent to the backend as-is. The backend
 * composes them into the list query, so an active filter dimension with no
 * matches (e.g. a customer with no locations) correctly collapses the result
 * set to zero instead of silently disabling the filter.
 */
export interface FulfillmentFilter {
  status?: FulfillmentOrderPhase | 'all';
  subStatuses?: FulfillmentOrderSubstatus[];
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  projectId?: string;
  fromLocationIds?: string[];
  fromCustomerIds?: string[];
  fromLocationCompanyTypes?: LocationCompanyType[];
  toLocationIds?: string[];
  toCustomerIds?: string[];
  toLocationCompanyTypes?: LocationCompanyType[];
  sort?: Sort<FulfillmentSortProperty>;
}

export const fulfillmentCycleTimeReportSchema = z.object({
  createdToOpened: cycleTimeMetricSchema,
  openToFirstShipmentShipped: cycleTimeMetricSchema,
  firstShipmentShippedToClose: cycleTimeMetricSchema,
  totalOrders: z.number(),
});

export type FulfillmentCycleTimeReport = z.infer<
  typeof fulfillmentCycleTimeReportSchema
>;

/**
 * Schema for creating a fulfillment order item.
 */
const createFulfillmentOrderItemSchema = z.object({
  itemId: z.string(),
  requestedQuantity: z.number().min(1),
});

/**
 * Schema for creating a fulfillment order.
 */
export const createFulfillmentOrderSchema = z.object({
  projectId: z.string().nullable(),
  fromLocationId: z.string(),
  toLocationId: z.string(),
  promiseShipDate: dateSchema.nullable(),
  promiseDeliveryDate: dateSchema.nullable(),
  orderNotes: z.string().nullable(),
  contactId: z.string().nullable(),
  items: z.array(createFulfillmentOrderItemSchema),
  fileIds: z.array(z.string()).optional(),
});

export type CreateFulfillmentOrderRequest = z.infer<
  typeof createFulfillmentOrderSchema
>;

/**
 * Schema for updating a fulfillment order item.
 */
const updateFulfillmentOrderItemSchema = z.object({
  itemId: z.string(),
  requestedQuantity: z.number().min(1).optional(),
});

/**
 * Schema for updating a fulfillment order.
 */
export const updateFulfillmentOrderSchema = z.object({
  projectId: z.string().nullable().optional(),
  fromLocationId: z.string().optional(),
  toLocationId: z.string().optional(),
  promiseShipDate: dateSchema.nullable().optional(),
  promiseDeliveryDate: dateSchema.nullable().optional(),
  orderNotes: z.string().nullable().optional(),
  contactId: z.string().nullable().optional(),
  items: z.array(updateFulfillmentOrderItemSchema).optional(),
});

export type UpdateFulfillmentOrderRequest = z.infer<
  typeof updateFulfillmentOrderSchema
>;
